/**
 * Singleton service which allows to display notifications (or toast, or alert, or however you call a small temp overlay message)
 *
 * It must only be used by getting the instance from the static getInstance method to behave as expected
 */
export class WcNotificationService {
    constructor() {
        this.notificationsQueue = [];
    }
    static getInstance() {
        // we rely on the window, instead of a static parameter to have it available across all modules
        if (window['WEB_COMPONENTS_NOTIFICATIONS'] === undefined) {
            window['WEB_COMPONENTS_NOTIFICATIONS'] = new WcNotificationService();
        }
        return window['WEB_COMPONENTS_NOTIFICATIONS'];
    }
    get isDisplayingNotification() {
        return this.displayedNotification !== undefined;
    }
    /**
     * Displays a fully configurable notification
     * @param config The configuration object of the Notification
     * @param bypassQueue Setting bypassQueue to `true` will dismiss any existing notification & display it before any other notification in the queue
     */
    showNotification(config, bypassQueue = false) {
        if (bypassQueue) {
            this.notificationsQueue.unshift(config);
            this.displayedNotification?.dismiss();
        }
        else {
            this.notificationsQueue.push(config);
        }
        if (this.isDisplayingNotification) {
            return;
        }
        this.renderNotification(this.notificationsQueue.shift());
    }
    /**
     * Displays a success notification
     * @param config The configuration object of the notification
     * @param bypassQueue see `showNotification`
     */
    showSuccess(config, bypassQueue = false) {
        this.showNotification({
            ...config,
            variant: 'success',
        }, bypassQueue);
    }
    /**
     * Displays a warning notification
     * @param config The configuration object of the notification
     * @param bypassQueue see `showNotification`
     */
    showWarning(config, bypassQueue = false) {
        this.showNotification({
            ...config,
            variant: 'warning',
        }, bypassQueue);
    }
    /**
     * Displays an information notification
     * @param config The configuration object of the notification
     * @param bypassQueue see `showNotification`
     */
    showInformation(config, bypassQueue = false) {
        this.showNotification({
            ...config,
            variant: 'information',
        }, bypassQueue);
    }
    /**
     * Displays an error (danger variant) notification
     * @param config The configuration object of the notification
     * @param bypassQueue see `showNotification`
     */
    showError(config, bypassQueue = false) {
        this.showNotification({
            ...config,
            variant: 'danger',
        }, bypassQueue);
    }
    hasNotificationInQueue() {
        return this.notificationsQueue.length > 0;
    }
    /**
     * Renders a notification and recursively render the next one in queue when dismissed
     * @param config The configuration object of the notification to display
     * @private
     */
    renderNotification(config) {
        this.displayedNotification = Object.assign(document.createElement('wc-notification'), config);
        document.body.append(this.displayedNotification);
        this.displayedNotification.show();
        this.displayedNotification.addEventListener('dismissed', () => {
            this.displayedNotification.remove();
            if (this.hasNotificationInQueue()) {
                this.renderNotification(this.notificationsQueue.shift());
            }
            else {
                this.displayedNotification = undefined;
            }
        });
    }
}
